/**
 * Access token storage and expiry.
 *
 * Tokens last 30 minutes and there is no refresh endpoint, so the app has to know
 * when the current one dies rather than discovering it through a failed request in
 * the middle of something. Decoding `exp` is a base64url parse, not a verification —
 * the signature is the server's business. We only need the clock.
 */

const TOKEN_KEY = 'autoaware.token';

/** Show the re-authentication prompt this long before the token actually dies. */
export const EXPIRY_WARNING_MS = 2 * 60 * 1000;

export function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    // Private browsing modes can throw on storage access.
    return null;
  }
}

export function writeToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* Session survives in memory for this tab; nothing better to do. */
  }
}

export function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
    // Remove the previous version's keys so an old session cannot linger.
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  } catch {
    /* ignore */
  }
}

interface JwtClaims {
  sub?: string;
  exp?: number;
}

function decodeClaims(token: string): JwtClaims | null {
  const payload = token.split('.')[1];
  if (!payload) return null;

  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const json = atob(padded);
    const parsed: unknown = JSON.parse(json);
    if (typeof parsed !== 'object' || parsed === null) return null;
    return parsed as JwtClaims;
  } catch {
    return null;
  }
}

/** Expiry as epoch milliseconds, or null if the token carries no usable `exp`. */
export function expiryOf(token: string): number | null {
  const claims = decodeClaims(token);
  if (!claims || typeof claims.exp !== 'number') return null;
  return claims.exp * 1000;
}

/** The email the token was issued for. The backend puts it in `sub`. */
export function subjectOf(token: string): string | null {
  return decodeClaims(token)?.sub ?? null;
}

export function isExpired(token: string, now: number = Date.now()): boolean {
  const expiry = expiryOf(token);
  // A token we cannot read is not assumed dead; the server is the authority, and
  // rejecting it here would lock out a user whose token is merely unusual.
  if (expiry === null) return false;
  return expiry <= now;
}

export function msUntilExpiry(token: string, now: number = Date.now()): number | null {
  const expiry = expiryOf(token);
  if (expiry === null) return null;
  return Math.max(0, expiry - now);
}
